import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Users, DollarSign, Clock } from 'lucide-react';

const MetricsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [deaths, setDeaths] = useState(0);
  const [marketSize, setMarketSize] = useState(0);
  const [responseTime, setResponseTime] = useState(0);

  useEffect(() => {
    if (isInView) {
      const deathsInterval = setInterval(() => {
        setDeaths(prev => prev >= 300 ? 300 : prev + 5);
      }, 20);

      const marketInterval = setInterval(() => {
        setMarketSize(prev => prev >= 269 ? 269 : prev + 4);
      }, 25);

      const timeInterval = setInterval(() => {
        setResponseTime(prev => prev >= 93 ? 93 : prev + 2);
      }, 30);

      return () => {
        clearInterval(deathsInterval);
        clearInterval(marketInterval);
        clearInterval(timeInterval);
      };
    }
  }, [isInView]);

  const metrics = [
    { 
      icon: Users, 
      value: `${deaths}K+`, 
      label: 'Preventable Deaths',
      sublabel: 'Every year in India',
      color: 'blue'
    },
    { 
      icon: DollarSign, 
      value: `$${marketSize}M+`, 
      label: 'Market Size by 2028',
      sublabel: 'Emergency health-tech',
      color: 'blue'
    },
    { 
      icon: Clock, 
      value: `${responseTime}%`, 
      label: 'Faster Response',
      sublabel: '4hrs → 6min with AI',
      color: 'blue'
    },
    { 
      icon: TrendingUp, 
      value: '24/7', 
      label: 'AI Availability',
      sublabel: 'Always-on triage support',
      color: 'blue'
    }
  ];

  return (
    <section ref={ref} className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 text-black">
            The Numbers That
            <br />
            <span className="text-blue-600">Matter</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Critical insights that drive our mission to save lives
            Every second counts in emergency healthcare. Here's the impact we're making.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  rotateY: 10,
                  z: 50
                }}
                className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all relative overflow-hidden"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-50" />
                
                <div className="relative z-10">
                  <div className="bg-blue-100 rounded-2xl p-4 w-fit mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  <motion.div
                    className="text-5xl font-bold mb-3"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 200 }}
                  >
                    <span className="text-blue-600">{metric.value}</span>
                  </motion.div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{metric.label}</h3>
                  <p className="text-sm text-gray-600">{metric.sublabel}</p>
                </div>

                {/* Pulse Effect */}
                <motion.div
                  className="absolute inset-0 border-2 border-blue-400 rounded-3xl"
                  animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.5 }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MetricsSection;
